import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, LocaleSchema } from "../common/primitives.js";
const EntitlementValueSchema = z.union([z.boolean(), z.int(), z.literal("basic"), z.null()]);
const MembershipSummarySchema = z.object({
  organizationId: IdSchema,
  organizationName: z.string().min(1).max(200),
  /** System or custom role key, e.g. `owner`, `engineer`. */
  roleKey: z.string().min(1).max(64),
  /** A custom role's name (ORGX-BE-01); system roles are named by the app's translations. */
  roleName: z.string().max(60).nullable().optional(),
  /** RBAC permission keys of this membership (docs/plan/10-permissions.md). */
  permissions: z.array(z.string().min(1).max(100)),
  entitlements: z.record(z.string(), EntitlementValueSchema),
  organizationStatus: z.enum(["trial", "active", "read_only", "suspended", "closed"])
});
const ExternalSpaceSummarySchema = z.object({
  spaceId: IdSchema,
  name: z.string().min(1).max(200),
  hostOrganizationId: IdSchema,
  hostOrganizationName: z.string().min(1).max(200)
});
let knownZones;
function isIanaTimeZone(value) {
  const parts = value.split("/");
  if (parts.length > 3 || !parts.every((p) => /^[A-Za-z][A-Za-z0-9_+-]*$/.test(p))) return false;
  knownZones ??= new Set(
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : []
  );
  if (knownZones.has(value)) return true;
  try {
    return new Intl.DateTimeFormat("en", { timeZone: value }).resolvedOptions().timeZone === value;
  } catch {
    return false;
  }
}
const TimeZoneSchema = z.string().max(64).refine(isIanaTimeZone, "an IANA time zone such as Africa/Tunis");
const DEFAULT_TIME_ZONE = "Africa/Tunis";
const DigitStyleSchema = z.enum(["latn", "arab"]);
const UserProfileSchema = z.object({
  id: IdSchema,
  displayName: z.string().min(1).max(200),
  email: z.email(),
  locale: LocaleSchema,
  avatarUrl: z.string().nullable(),
  /** ORG-BE-07: additive fields (the defaults keep older fixtures valid). */
  timeZone: TimeZoneSchema.default(DEFAULT_TIME_ZONE),
  digitStyle: DigitStyleSchema.default("latn")
});
const UpdateProfileRequestSchema = z.strictObject({
  displayName: z.string().trim().min(1).max(200).optional(),
  locale: LocaleSchema.optional(),
  timeZone: TimeZoneSchema.optional(),
  digitStyle: DigitStyleSchema.optional()
}).refine((body) => Object.keys(body).length > 0, "change at least one field");
const MeResponseSchema = z.object({
  user: UserProfileSchema,
  memberships: z.array(MembershipSummarySchema),
  externalSpaces: z.array(ExternalSpaceSummarySchema),
  /**
   * A guest account (invited by email to another organisation's space, COL-BE-03) with no
   * organisation of its own: the web app shows the restricted guest shell (COL-FE-03).
   */
  guest: z.boolean().default(false),
  /**
   * A space invitation addressed to this person (named or guest, sent to their verified e-mail) is
   * waiting for their answer: a person without organisation is sent to the invitations page, not
   * to the creation of an organisation (COL-BE-03). Only a yes/no: nothing about the invitation.
   */
  pendingSpaceInvitation: z.boolean().default(false),
  session: z.object({
    /** Absolute expiry of the session. */
    expiresAt: IsoDateTimeSchema,
    /** Last successful step-up (second factor), if any. */
    steppedUpAt: IsoDateTimeSchema.nullable(),
    /** Authentication methods references from Keycloak (`pwd`, `otp`, `hwk`…). */
    amr: z.array(z.string()),
    /** The organisation policy requires a second factor the user has not set up yet. */
    mfaRequired: z.boolean(),
    /**
     * BFF path that opens the authenticator set-up (the client appends `&returnTo=`); null when
     * no second factor is required (AUTH-BE-05).
     */
    mfaEnrolmentUrl: z.string().max(200).nullable().default(null)
  }),
  /** Token to send in X-CSRF-Token on unsafe requests. */
  csrfToken: z.string().min(16).max(200)
});
const RECENT_AUTH_MINUTES = 10;
const AccountAuthenticatorSchema = z.strictObject({
  /** Keycloak's credential id. */
  id: z.uuid(),
  kind: z.enum(["totp", "security_key"]),
  /** The name the person gave it in Keycloak, if any. */
  label: z.string().max(255).nullable(),
  createdAt: IsoDateTimeSchema.nullable()
});
const AccountSecuritySchema = z.strictObject({
  password: z.strictObject({
    configured: z.boolean(),
    updatedAt: IsoDateTimeSchema.nullable()
  }),
  authenticators: z.array(AccountAuthenticatorSchema).max(20),
  /** An organisation of the person requires two-step verification. */
  mfaRequired: z.boolean(),
  /** End of the window opened by the last fresh sign-in (null: sign in again first). */
  recentAuthUntil: IsoDateTimeSchema.nullable()
});
const ChangePasswordRequestSchema = z.strictObject({
  password: z.string().min(12).max(128),
  /** Also sign out every other device (BatiFlow sessions and Keycloak sessions). */
  signOutOthers: z.boolean().default(true)
});
const ChangePasswordResponseSchema = z.strictObject({
  /** Other sessions that were ended. */
  otherSessionsEnded: z.int().min(0)
});
const AuthenticatorParamsSchema = z.strictObject({ authenticatorId: z.uuid() });
export {
  AccountAuthenticatorSchema,
  AccountSecuritySchema,
  AuthenticatorParamsSchema,
  ChangePasswordRequestSchema,
  ChangePasswordResponseSchema,
  DEFAULT_TIME_ZONE,
  DigitStyleSchema,
  EntitlementValueSchema,
  ExternalSpaceSummarySchema,
  MeResponseSchema,
  MembershipSummarySchema,
  RECENT_AUTH_MINUTES,
  TimeZoneSchema,
  UpdateProfileRequestSchema,
  UserProfileSchema,
  isIanaTimeZone
};
//# sourceMappingURL=index.js.map